import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import type { QuickSearchResult } from '@frameio/graphql-client/generated';
import type { ListLoadingStates } from '@frameio/vapor';
import { useQuickSearchActiveTab } from '../../shared/contexts';
import type { SectionId } from '../../shared/types';
import { LEXICAL_SECTION_ID, NLP_SECTION_ID } from '../../shared/types';
import type {
  QuickSearchMachineOutput,
  QuickSearchTabbedOutput,
} from '../machines/types';
import { usePreferredTab } from './usePreferredTab';

type QuickSearchTabbedViewModel = {
  onTabChange: (targetSectionId: SectionId) => void;
  shouldShowLexicalDivider: false;
  showTabBar: boolean;
  visibleItems: QuickSearchResult[];
  visibleLoadingStatus: ListLoadingStates | undefined;
  visibleSectionId: SectionId;
};

type UseQuickSearchTabbedViewModelProps = {
  searchResults: QuickSearchMachineOutput;
};

const getVisibleSectionId = ({
  activeTab,
  mediaIntelligenceResultsCount,
  nlpLoading,
  preferredTab,
  standardResultsCount,
  lexicalLoading,
}: {
  activeTab: SectionId | null;
  mediaIntelligenceResultsCount: number;
  nlpLoading: boolean;
  preferredTab: SectionId | null;
  standardResultsCount: number;
  lexicalLoading: boolean;
}): SectionId => {
  if (activeTab !== null) return activeTab;

  if (preferredTab === NLP_SECTION_ID) {
    if (nlpLoading || mediaIntelligenceResultsCount > 0) return NLP_SECTION_ID;
    // AI settled empty — render Standard so its skeleton or empty state shows.
    return LEXICAL_SECTION_ID;
  }

  if (preferredTab === LEXICAL_SECTION_ID) {
    if (lexicalLoading || standardResultsCount > 0) return LEXICAL_SECTION_ID;
    // Standard settled empty — render AI so its skeleton or empty state shows.
    return NLP_SECTION_ID;
  }

  return LEXICAL_SECTION_ID;
};

/**
 * Resolves the tabbed quick-search dialog state into a small UI-facing view
 * model. This keeps tab selection policy out of the dialog component while
 * leaving persistence and machine state in their existing owners.
 */
export function useQuickSearchTabbedViewModel({
  searchResults,
}: UseQuickSearchTabbedViewModelProps): QuickSearchTabbedViewModel | null {
  const { activeTab, setActiveTab } = useQuickSearchActiveTab();
  const { preferredTab, setPreferredTab } = usePreferredTab();
  const tabbedSearchResults: QuickSearchTabbedOutput | null =
    searchResults.listMode === 'MULTI_TABBED' ? searchResults : null;

  const tabbedLexicalLoading = tabbedSearchResults?.isLexicalLoading;
  const tabbedNlpLoading = tabbedSearchResults?.isNlpLoading;

  // Guards auto-redirect so a manual tab click isn't overridden.
  const userSelectedRef = useRef(false);
  const wasLoadingRef = useRef(false);

  const handleSwitchTab = useCallback(
    (targetSectionId: SectionId) => {
      userSelectedRef.current = true;
      setActiveTab(targetSectionId);
      setPreferredTab(targetSectionId);
    },
    [setActiveTab, setPreferredTab]
  );

  useLayoutEffect(() => {
    if (!tabbedSearchResults) return;

    const lexicalDone = !tabbedLexicalLoading;
    const nlpDone = !tabbedNlpLoading;
    const hasStandard = tabbedSearchResults.standardResultsCount > 0;
    const hasAI = tabbedSearchResults.mediaIntelligenceResultsCount > 0;
    const isLoading = !lexicalDone || !nlpDone;

    // Reset the manual-selection guard only when a new loading cycle starts.
    if (isLoading && !wasLoadingRef.current) {
      userSelectedRef.current = false;
    }
    wasLoadingRef.current = isLoading;

    // Auto-redirect: active tab settled empty, other has results, user hasn't
    // manually picked a tab this cycle.
    if (
      activeTab !== null &&
      lexicalDone &&
      nlpDone &&
      !userSelectedRef.current
    ) {
      if (activeTab === LEXICAL_SECTION_ID && !hasStandard && hasAI) {
        setActiveTab(NLP_SECTION_ID);
        return;
      }
      if (activeTab === NLP_SECTION_ID && !hasAI && hasStandard) {
        setActiveTab(LEXICAL_SECTION_ID);
        return;
      }
    }

    // Tab already selected — nothing else to decide.
    if (activeTab !== null) return;

    // Preferred tab ready — select it immediately.
    if (preferredTab === LEXICAL_SECTION_ID && lexicalDone && hasStandard) {
      setActiveTab(LEXICAL_SECTION_ID);
      return;
    }
    if (preferredTab === NLP_SECTION_ID && nlpDone && hasAI) {
      setActiveTab(NLP_SECTION_ID);
      return;
    }

    // No preference — select Standard as soon as it settles with results.
    if (preferredTab === null && lexicalDone && hasStandard) {
      setActiveTab(LEXICAL_SECTION_ID);
      return;
    }

    // Wait for both to settle before choosing a fallback.
    if (!lexicalDone || !nlpDone) return;

    if (hasStandard && hasAI) {
      setActiveTab(preferredTab ?? LEXICAL_SECTION_ID);
    } else if (hasStandard) {
      setActiveTab(LEXICAL_SECTION_ID);
    } else if (hasAI) {
      setActiveTab(NLP_SECTION_ID);
    }
  }, [
    tabbedSearchResults,
    tabbedLexicalLoading,
    tabbedNlpLoading,
    activeTab,
    preferredTab,
    setActiveTab,
  ]);

  return useMemo(() => {
    if (!tabbedSearchResults) return null;

    const visibleSectionId = getVisibleSectionId({
      activeTab,
      lexicalLoading: Boolean(tabbedLexicalLoading),
      mediaIntelligenceResultsCount:
        tabbedSearchResults.mediaIntelligenceResultsCount,
      nlpLoading: Boolean(tabbedNlpLoading),
      preferredTab,
      standardResultsCount: tabbedSearchResults.standardResultsCount,
    });

    return {
      onTabChange: handleSwitchTab,
      shouldShowLexicalDivider: false,
      showTabBar: true,
      visibleItems: tabbedSearchResults.tabItemsById[visibleSectionId],
      visibleLoadingStatus:
        tabbedSearchResults.tabLoadingStatusBySectionId[visibleSectionId],
      visibleSectionId,
    };
  }, [
    tabbedSearchResults,
    activeTab,
    preferredTab,
    tabbedLexicalLoading,
    tabbedNlpLoading,
    handleSwitchTab,
  ]);
}
